import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ConceptTableModel } from 'src/app/models/tables/management/concept-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ConceptService } from 'src/app/services/management/concept.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-concept',
  templateUrl: './concept.component.html',
  styleUrls: ['./concept.component.scss'],
  standalone: false
})
export class ConceptComponent implements OnInit {

  ascendant: boolean = true;
  columnSort: string | null = null;
  data: PagedResponseModel<ConceptTableModel> = new PagedResponseModel<ConceptTableModel>();
  header: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Conceptos';
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }, {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  openImportConstructionSiteModal: boolean = false;
  url: string = 'concept';

  constructor(private confirmDialogService: ConfirmDialogService,
    private headerService: HeaderService,
    private conceptService: ConceptService,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllConceptsWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async delete(concept: ConceptTableModel) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `No tiene permisos para eliminar conceptos.
          Comuníquese con el administrador del sistema.`);
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el concepto?')) {
      try {
        await firstValueFrom(this.conceptService.deleteConcept(concept.conceptId));
        await this.getAllConceptsWithPagination();
        this.toastHandlerService.showToast('success', 'El concepto fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el concepto.', error);
      }
    }
  }

  async getAllConceptsWithPagination() {
    try {
      this.data = await firstValueFrom(this.conceptService.getAllConceptsWithPagination(this.page, this.limit,
        this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los conceptos.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(this.conceptService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de conceptos.', error);
    }
  }

  async getPermission() {
    const url = this.router.url as string
    this.url = url.substring(1);
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('warning', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      this.permissionUpdatable = false;
      console.error('Error al cargar permisos.', error);
    }
  }

  onAction(event: { action: string, item?: ConceptTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
    if (event.action === 'delete' && event.item) {
      this.delete(event.item);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllConceptsWithPagination();
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
